import React from 'react';
import useReactRouter from 'use-react-router';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'jbc-front/components/Button';

import { useEmployeeMutation as useMutation } from '@jbc-year-end-adj/2024/hooks/graphql/useMutation';
import { Textarea } from '@jbc-year-end-adj/2024/components/form/Textarea';
import { Finish as FinishTemplate } from '@jbc-year-end-adj/2024/features/questions/templates/Finish';
import { ButtonContainer } from '@jbc-year-end-adj/2024/features/questions/components/ButtonContainer';
import { useEmployeeQuery as useQuery } from '@jbc-year-end-adj/2024/hooks/graphql/useQuery';

import { LoadingPage } from '../../Loading';
import { FETCH_FINISH, QueryResult } from './query';
import { APPLY_REQUEST } from './mutation';
import { ReportList } from './ReportList';
import { Schema, schema } from './schema';
import styles from './Finish.scss';

// @ts-ignore
import { Link } from 'react-router-dom';

export const Finish = () => {
  const { history } = useReactRouter();
  const [answer, { loading: loadingApply }] = useMutation(APPLY_REQUEST);
  const { data, loading } = useQuery<QueryResult>(FETCH_FINISH, {
    fetchPolicy: 'no-cache'
  });
  const {
    register,
    formState: { errors },
    handleSubmit
  } = useForm<Schema>({
    resolver: yupResolver(schema)
  });

  if (loading) return <LoadingPage />;
  if (!data) throw Error;

  const isWithholdingSlipInOrder = data.request?.profile?.formerJobs?.some(formerJob => formerJob.withholdingSlipStatus === 'in_order');
  const isWithholdingSlipUnavailable = data.request?.profile?.formerJobs?.some(
    formerJob => formerJob.withholdingSlipStatus === 'unavailable'
  );

  const {
    request,
    request: {
      employee: { status },
      employee
    }
  } = data;

  if (status === 'not_requested') return null;
  const profile = ['in_progress', 'applying', 'rejected', 'fixed'].includes(status) ? request.profile : employee.profile;

  const onSubmit: SubmitHandler<Schema> = async data => {
    await answer({
      variables: { comment: data.comment || null }
    });
    history.push('/employee_input');
  };

  return (
    <FinishTemplate>
      {!profile || (!profile.needAdjThisYear && !profile.nextYearOnly) ? null : (
        <>
          <p>情報を確認・提出してください</p>
          <ReportList
            needAdjThisYear={profile.needAdjThisYear}
            nextYearOnly={profile.nextYearOnly}
            isNotCoveredByNextYear={profile.isNotCoveredByNextYear}
            isWithholdingSlipUnavailable={isWithholdingSlipUnavailable}
          />
          <p>
            各申告書を確認し、誤りがない場合<b>画面下段の「情報を提出」ボタン</b>をクリックしてください。
            <br />
            申告書に誤りがある場合、ナビゲーションバーから入力画面に戻り情報を修正してください。
          </p>
        </>
      )}

      {isWithholdingSlipInOrder && (
        <div className={styles.alert}>
          <p>
            <Link to="/employee_input?current=former_jobs">【注意】STEP 2-3 前職情報</Link>
            で源泉徴収票が【取り寄せ中】になっています。源泉徴収票の入手を待たずに年末調整を提出してよろしいですか？
          </p>
        </div>
      )}

      <Textarea
        label="管理者への連絡事項等"
        placeholder="管理者へ伝えたい内容があれば記載してください"
        {...register('comment')}
        error={errors.comment?.message}
        isError={!!errors.comment?.message}
      />

      <ButtonContainer>
        <Button primary onClick={handleSubmit(onSubmit)} disabled={loadingApply}>
          情報を提出
        </Button>
      </ButtonContainer>
    </FinishTemplate>
  );
};
